import React, { FunctionComponent, useEffect, useState } from 'react';
import Collapsible from 'react-collapsible';
import styled, { ThemeProvider } from 'styled-components';
import { Box, Flex, Text } from 'rebass/styled-components';
import theme from 'styled-theming';
import {
  PolicyResultComponent,
  PolicyVariablesTable,
  POLICY_EXECUTION_TABLE_COLUMN_WIDTHS,
  PolicyExecutionProps,
  PolicyCondition,
  policyActionResultParser,
  PolicyAction,
} from '.';
import { CollapsibleDrawerArrow, HorizontalRule } from '..';
import { PolicyEvalDisposition } from '../../enums';
import { useAppState } from '../../hooks';
import { darkTheme, lightTheme } from '../../theme';

const arrowTextColor = theme('mode', {
  light: (props: any) => props.theme.colors.periwinkle,
  dark: (props: any) => props.theme.colors.brightCerulean,
});

const ArrowContainer = styled(Box)`
  color: ${arrowTextColor};
`;

const PolicyExecution: FunctionComponent<
PolicyExecutionProps
> = ({
  policyRun, policyVariables, policyExceptions, isFirst, isLast, measure,
}: PolicyExecutionProps) => {
  const [isOpen, setOpen] = useState<boolean>(false);
  const [isHover, setHover] = useState<boolean>(false);
  const { themeMode } = useAppState();

  const variables = policyRun.variables?.concat(policyRun?.transientVariables
    ? Object.keys(policyRun.transientVariables) : []);

  const policyEvalDisposition: PolicyEvalDisposition = policyActionResultParser({
    result: policyRun.evalResult, exception: policyRun.exception,
  });
  const isEmphasizedPolicyResult: boolean = policyEvalDisposition === PolicyEvalDisposition.MATCH;

  useEffect(() => {
    measure();
  }, [measure]);

  return (
    <Box
      data-testid="policy-execution-run"
      pb={2}
      pt={2}
      sx={{ position: 'relative', overflowY: 'hidden', minHeight: '42px' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {!isFirst && (
      <Box sx={{
        position: 'absolute',
        top: '-5px',
        left: '49px',
        height: '16px',
        width: '2px',
        background: 'linear-gradient(180deg, rgba(104, 112, 146, 0.62) 0%, rgba(104, 112, 146, 0.31) 100%)',
      }}
      />
      )}
      {!isLast && (
      <Box sx={{
        position: 'absolute',
        top: '26px',
        left: '49px',
        height: '100%',
        width: '2px',
        background: 'linear-gradient(180deg, rgba(104, 112, 146, 0.62) 0%, rgba(104, 112, 146, 0.31) 100%)',
      }}
      />
      )}
      <Collapsible
        trigger={(
          <Box mb={2}>
            <Flex wrap="nowrap" pr={2}>
              <Box
                data-testid="policy-run-eval-result"
                width={POLICY_EXECUTION_TABLE_COLUMN_WIDTHS[0]}
                variant="flexTableCell"
              >
                <PolicyResultComponent
                  policyEvalDisposition={policyEvalDisposition}
                  order={policyRun.policy?.priority}
                  isEmphasizedPolicyResult={isEmphasizedPolicyResult}
                />
              </Box>
              <Box
                data-testid="policy-run-policy-name"
                width={POLICY_EXECUTION_TABLE_COLUMN_WIDTHS[1]}
                variant="flexTableCell"
                pr={6}
              >
                <Text
                  variant={isEmphasizedPolicyResult ? 'primaryBold' : 'primary'}
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {policyRun.policy?.name}
                </Text>
              </Box>
              <Box
                data-testid="policy-run-policy-condition"
                width={POLICY_EXECUTION_TABLE_COLUMN_WIDTHS[2]}
                variant="flexTableCell"
                pr={6}
              >
                <PolicyCondition
                  condition={policyRun.policy?.condition}
                  variables={policyRun.variables}
                  transientVariables={policyRun.transientVariables}
                  valueMap={policyVariables}
                  exceptionsMap={policyExceptions}
                  isEmphasizedPolicyResult={isEmphasizedPolicyResult}
                />
              </Box>
              <Box
                data-testid="policy-run-policy-actions"
                width={POLICY_EXECUTION_TABLE_COLUMN_WIDTHS[3]}
                variant="flexTableCell"
                pr={4}
              >
                <PolicyAction
                  isEmphasizedPolicyResult={isEmphasizedPolicyResult}
                  actions={policyRun.evalResult}
                />
              </Box>
              <Box
                width={POLICY_EXECUTION_TABLE_COLUMN_WIDTHS[4]}
                variant="flexTableCell"
                onClick={() => {
                  if (policyRun.variables && policyRun.variables.length) {
                    setOpen((prev: boolean) => !prev);
                  }
                }}
                sx={{
                  cursor: 'pointer',
                  visibility: policyRun.variables && policyRun.variables.length && (isHover || isOpen) ? 'visible' : 'hidden',
                }}
                data-testid="policy-logs-open-variables-table"
              >
                <ArrowContainer><CollapsibleDrawerArrow isOpen={isOpen} /></ArrowContainer>
              </Box>
            </Flex>
          </Box>
      )}
        handleTriggerClick={() => {}} // disable default trigger
        open={isOpen}
        transitionTime={1}
        onOpen={measure}
        onClose={measure}
      >
        <Box ml={POLICY_EXECUTION_TABLE_COLUMN_WIDTHS[0]} pt={2}>
          <HorizontalRule />
          <ThemeProvider theme={themeMode === 'dark' ? darkTheme : lightTheme}>
            <PolicyVariablesTable
              variables={variables}
              valueMap={{ ...policyVariables, ...policyRun.transientVariables }}
              exceptionsMap={policyExceptions}
            />
          </ThemeProvider>
          <HorizontalRule />
        </Box>
      </Collapsible>
    </Box>
  );
};

export default PolicyExecution;
